/**
 * The keyboard layer — DESIGN.md §13.3/§13.5, Long-Day Review workstream 5.
 *
 * One flat table, one listener, no chords and no modes: every shortcut in the
 * product is a row in an array that the `?` sheet renders verbatim, so the
 * help can never drift from the behaviour.
 *
 * The focus-return helpers live here too because they are the other half of
 * the same problem: a keyboard user who opens a dialog and closes it must land
 * back where they were, not at <body> (KEYS-2).
 */
import { useCallback, useEffect, useRef } from 'react';

export const IS_MAC =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

export interface HotkeyBinding {
  /** `event.key`, matched case-insensitively. e.g. `c`, `/`, `?`, `Escape`. */
  key: string;
  /** ⌘ on a Mac, Ctrl everywhere else. */
  mod?: boolean;
  /** Only meaningful for letters; punctuation carries its own shift already. */
  shift?: boolean;
  /**
   * Return `false` for "nothing to do here" — the keystroke is then left to
   * the browser, so `Enter` on a button still activates it.
   */
  run: (event: KeyboardEvent) => void | boolean;
  /** One line for the `?` sheet. */
  description: string;
  /** Heading it is listed under in the `?` sheet. */
  group: string;
  /** Esc only: still fires while a dialog/menu owns the screen. */
  allowInDialog?: boolean;
  /** Kept out of the `?` sheet (duplicate rows, e.g. `/` and ⌘K). */
  hidden?: boolean;
}

/** Where a keystroke belongs to the control, not to the app. */
const TYPING_SELECTOR = 'input, textarea, select, [contenteditable=true], [role=combobox]';

/**
 * …except the inputs that do not swallow characters. A focused checkbox is the
 * normal state after picking rows for a bulk action, and Esc there has to
 * clear the selection (KEYS-18) rather than be treated as typing.
 */
const NON_TYPING_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'file',
  'range',
  'color',
  'image',
]);

/** A Radix overlay is open and owns the keyboard. */
const OVERLAY_SELECTOR = '[data-state=open][role=dialog], [role=menu], [role=listbox]';

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  // `closest` rather than `matches`: tiptap puts the caret in a child node of
  // the [contenteditable] host.
  const field = target.closest(TYPING_SELECTOR);
  if (!field) return false;
  if (field instanceof HTMLInputElement && NON_TYPING_INPUT_TYPES.has(field.type)) return false;
  return true;
}

export function isOverlayOpen(): boolean {
  return Boolean(document.querySelector(OVERLAY_SELECTOR));
}

/** ⌘K / Ctrl+K, and the printable keys as themselves. */
export function formatChord(binding: Pick<HotkeyBinding, 'key' | 'mod' | 'shift'>): string {
  const parts: string[] = [];
  if (binding.mod) parts.push(IS_MAC ? '⌘' : 'Ctrl');
  if (binding.shift) parts.push(IS_MAC ? '⇧' : 'Shift');
  const key = binding.key;
  const label =
    key === 'Escape' ? 'Esc' : key === ' ' ? 'Space' : key.length === 1 ? key.toUpperCase() : key;
  parts.push(label);
  return IS_MAC ? parts.join('') : parts.join('+');
}

function matches(binding: HotkeyBinding, event: KeyboardEvent): boolean {
  if (event.key.toLowerCase() !== binding.key.toLowerCase()) return false;

  const mod = IS_MAC ? event.metaKey : event.ctrlKey;
  if (binding.mod) {
    if (!mod) return false;
  } else if (event.metaKey || event.ctrlKey || event.altKey) {
    return false;
  }

  // Shift is only checked for letters: `?` and `_` cannot be typed without it,
  // so demanding `shift: false` there would make them unreachable.
  const isLetter = binding.key.length === 1 && /[a-z0-9]/i.test(binding.key);
  if (isLetter && event.shiftKey !== Boolean(binding.shift)) return false;

  return true;
}

/**
 * One window listener for the whole table. Bindings are read through a ref so
 * that closures over fresh props do not add and remove listeners on every
 * render (which loses keystrokes typed during the gap).
 */
export function useHotkeys(bindings: HotkeyBinding[]): void {
  const ref = useRef(bindings);
  ref.current = bindings;

  useEffect(() => {
    // Installed here so the opener of the *first* dialog of the session is
    // already recorded (see trackFocusOrigin).
    trackFocusOrigin();

    const onKeyDown = (event: KeyboardEvent): void => {
      // Autorepeat is deliberately allowed: holding j walks a list.
      if (event.isComposing) return;
      // Radix dismisses a dialog/menu on Esc in the document *capture* phase
      // and preventDefaults the event; by the time it reaches the window the
      // overlay is gone, so `isOverlayOpen()` alone let Esc fall through and
      // close the card peek behind the dialog too.
      if (event.defaultPrevented) return;
      const typing = isTypingTarget(event.target);
      const overlay = isOverlayOpen();

      for (const binding of ref.current) {
        if (!matches(binding, event)) continue;
        // A modified chord (⌘K) is still safe to fire inside a text field —
        // single letters are not.
        if (typing && !binding.mod && !binding.allowInDialog) continue;
        if (overlay && !binding.allowInDialog) continue;
        // Run first, then swallow the key — a binding that declines (`false`)
        // passes on to the next row, and finally to the browser.
        if (binding.run(event) === false) continue;
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}

/* ------------------------------------------------------------------------ */
/* Focus return — KEYS-2                                                      */
/* ------------------------------------------------------------------------ */

/**
 * Radix restores focus to whatever was focused when its FocusScope mounted,
 * which is the right answer right up until the opener unmounts (a row that
 * re-renders, a menu item that closed with the menu) — then focus lands on
 * <body> and the next Tab restarts at the sidebar. So we track the last
 * focused element *outside* any overlay ourselves, and hand it back on close.
 */
let lastOutsideFocus: HTMLElement | null = null;
let tracking = false;

export function trackFocusOrigin(): void {
  if (tracking || typeof document === 'undefined') return;
  tracking = true;
  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.closest('[role=dialog], [role=menu], [data-radix-popper-content-wrapper]')) return;
      lastOutsideFocus = target;
    },
    true,
  );
}

export function currentFocusOrigin(): HTMLElement | null {
  return lastOutsideFocus;
}

function isFocusable(element: HTMLElement | null): element is HTMLElement {
  return Boolean(element?.isConnected && element.getClientRects().length > 0);
}

/**
 * Give focus back to `opener`, or to the nearest sensible landmark if it has
 * gone away. Never returns without having moved focus off <body>.
 */
export function returnFocus(event: Event, opener: HTMLElement | null): void {
  if (event.defaultPrevented) return;

  if (isFocusable(opener)) {
    event.preventDefault();
    opener.focus({ preventScroll: false });
    return;
  }

  const fallback =
    document.querySelector<HTMLElement>('[data-card-panel]') ??
    document.getElementById('main') ??
    document.querySelector<HTMLElement>('main');
  if (!fallback) return;

  event.preventDefault();
  if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
  fallback.focus({ preventScroll: true });
}

/**
 * `onCloseAutoFocus` for a Radix dialog: hands focus back to the last thing
 * focused outside any overlay.
 *
 * Note that it reads the origin when the dialog *closes*, not when the
 * component renders. A `<DialogContent>` element is created by its parent long
 * before the dialog opens (only the portal's children are mounted lazily), so
 * capturing at render time reliably captured `null` — which is precisely why
 * closing a dialog dumped focus on <body> (KEYS-2). Focus moving *into* the
 * dialog is filtered out of the tracker, so the origin is still the opener.
 */
export function useDialogFocusReturn(): (event: Event) => void {
  trackFocusOrigin();
  return useCallback((event: Event) => returnFocus(event, currentFocusOrigin()), []);
}
